/**
 * Live-presence heartbeat — Feb 2026.
 *
 * Every logged-in browser tab pings `POST /api/presence/heartbeat`
 * once a minute while it's the active tab. Combined with the passive
 * "bump last_seen_at on any authenticated request" tracking in the
 * backend, this drives the admin "● N online" chip in the header.
 *
 * Behaviour:
 *  • Fires immediately on mount (so the first render's chip is fresh).
 *  • Then every 60 seconds.
 *  • Skips heartbeats while the tab is hidden (Page Visibility API).
 *  • Re-fires on tab focus / visibility-return so a returning user
 *    lights back up within 1 second, not the next 60-second slot.
 *  • Silently swallows errors — presence is best-effort.
 */
import { useEffect, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const HEARTBEAT_INTERVAL_MS = 60_000;

export function usePresenceHeartbeat() {
  const { user } = useAuth();
  const timerRef = useRef(null);
  const lastSentRef = useRef(0);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const send = () => {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      const now = Date.now();
      // Guard against double-fire (mount + focus race)
      if (now - lastSentRef.current < 5_000) return;
      lastSentRef.current = now;
      api.post("/presence/heartbeat").catch(() => { /* best-effort */ });
    };

    send(); // fire immediately
    timerRef.current = setInterval(send, HEARTBEAT_INTERVAL_MS);
    const onFocus = () => send();
    const onVisibility = () => {
      if (document.visibilityState === "visible") send();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user?.id]);
}
